'use client';

import { useContext, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  motion,
  useTransform,
  useReducedMotion,
  cubicBezier,
} from 'motion/react';

import { useDimensions } from '../../context/ThemeContextManagement';
import { useCustomScroll } from '../../utils/useCustomScroll';
import { calculateDelay, CUSTOM_EASE } from '../../utils/animations';
import { ComponentInViewContext } from '../component-wrapper/ComponentWrapper';

const PARALLAX_RANGE = 10;

/**
 * The scroll-driven variant, extracted so its hooks only mount when parallax is
 * actually requested. CustomImage has ~34 render sites and only ~15 pass
 * `parallax`, so calling useCustomScroll unconditionally in the parent meant a
 * scroll listener and two transforms per image site-wide.
 *
 * Uses useCustomScroll rather than motion's useScroll on purpose: it freezes the
 * progress value while the subtree is being removed by AnimatePresence.
 */
function ParallaxFigure({
  className,
  style,
  parentRef,
  a11yProps,
  motionProps,
  extraProps,
  children,
}) {
  const boxRef = useRef(null);
  const dimensions = useDimensions();

  const { scrollYProgress } = useCustomScroll({
    target: parentRef || boxRef,
    offset: [`start ${dimensions?.lvhHeight ?? 0}px`, 'end start'],
  });

  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [`-${PARALLAX_RANGE}%`, `${PARALLAX_RANGE}%`]
  );
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 1]);

  return (
    <motion.figure
      className={className}
      ref={boxRef}
      style={style}
      {...a11yProps}
      {...motionProps}
      {...extraProps}
    >
      <motion.div
        style={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          y,
          scale,
        }}
      >
        {children}
      </motion.div>
    </motion.figure>
  );
}

export default function CustomImage({
  image,
  sourceUrl,
  fill = false,
  forceHeight = false,
  fillMedia = true,
  forceLoad = false,
  // Explicit `fetchpriority` hint for the browser. Set to 'high' for the LCP
  // image (e.g. the hero image on the first block) so it's fetched ahead of
  // other resources. Leave undefined to let the browser/Next decide.
  fetchPriority,
  // Next's own default is 75 and, through AVIF/WebP, it is visually fine for
  // ordinary content. This used to be hardcoded at 90 for every image on the
  // site. Pass 90 explicitly for images that earn it — a full-bleed hero, a
  // video poster — rather than paying for it everywhere. Only the values in
  // `images.qualities` in next.config.js are permitted.
  quality = 75,
  className = 'MediaItem',
  targetWidth = false,
  targetHeight = false,
  parallax = false,
  swiperParallax = false,
  parentRef = false,
  // How wide the image renders on desktop, as a % of the viewport.
  // Pass the number/string that matches your layout: 100 (full), 50 (half),
  // 33 (third), 25 (quarter), etc. Mobile defaults to full-width — see
  // `mobileWidth` to override.
  // Used to auto-generate the `sizes` attribute so Next can pick the right
  // resolution and the "sizes of 100vw but not full width" warning goes away.
  desktopWidth = 100,
  // How wide the image renders on mobile (≤768px), as a % of the viewport.
  // Defaults to 100 (full-width). Set it lower when the image is only part of
  // the viewport on small screens too, so Next doesn't over-fetch on mobile.
  mobileWidth = 100,
  // Explicit `sizes` override. When provided it wins over
  // `desktopWidth`/`mobileWidth` — use it for bespoke breakpoints
  // (e.g. "(max-width: 768px) 80vw, 280px").
  sizes,
  reveal = false,
  index,
  animationOrder = 0,
  delay = 0,
  duration = 0.9,
  ...props
}) {
  // Resolve the responsive `sizes` attribute.
  const dw = parseInt(desktopWidth, 10) || 100;
  const mw = parseInt(mobileWidth, 10) || 100;
  const resolvedSizes =
    sizes ??
    (dw >= 100 && mw >= 100 ? '100vw' : `(max-width: 768px) ${mw}vw, ${dw}vw`);

  const src = sourceUrl ?? image?.sourceUrl ?? false;
  const width = image?.width;
  const height = image?.height;
  const alt = image?.alt ?? '';
  const blurPlaceholder = image?.blurPlaceholder;

  // An image with no alt text is decorative and conveys no information to
  // screen reader users. Hide the whole <figure> from the accessibility tree
  // so it isn't announced as an empty "figure"/"group". Callers can still
  // override by passing an explicit `aria-hidden` prop.
  const decorative = !alt || alt.trim() === '';
  const a11yProps = decorative ? { 'aria-hidden': 'true' } : {};

  // ─── Clip-path reveal (opt-in, mirrors AnimateSplitLines) ─────────────────
  const hasPlayed = useRef(false);
  const isInView = useContext(ComponentInViewContext) ?? true;
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (isInView) hasPlayed.current = true;
  }, [isInView]);

  const easing = cubicBezier(...CUSTOM_EASE);

  const revealVariants = {
    hidden: { clipPath: 'inset(100% 0% 0% 0%)' },
    visible: {
      clipPath: 'inset(0% 0% 0% 0%)',
      transition: {
        delay: calculateDelay(animationOrder, index, delay),
        duration,
        ease: easing,
      },
    },
  };

  const revealMotionProps =
    reveal && !reduceMotion
      ? {
          variants: revealVariants,
          initial: hasPlayed.current ? false : 'hidden',
          animate: isInView ? 'visible' : 'hidden',
        }
      : {};

  // ─── Placeholder ─────────────────────────────────────────────────────────
  if (!src) {
    return (
      <figure
        className={className}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'clip',
          top: 0,
          left: 0,
        }}
        {...props}
      >
        <Image
          alt=""
          className="placeholder"
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
          fill
          style={{ objectFit: 'cover' }}
        />
      </figure>
    );
  }

  // ─── Figure / image prop calculation ─────────────────────────────────────
  let figureStyle = {};
  let imageProps = {};

  if (fill) {
    figureStyle = {
      position: 'absolute',
      width: '100%',
      height: forceHeight ? `${height}px` : '100%',
      overflow: 'clip',
      top: 0,
      left: 0,
    };
    imageProps.fill = true;
    imageProps.style = { objectFit: fillMedia ? 'cover' : 'contain' };
  } else if (forceHeight) {
    const aspectRatio =
      targetWidth && targetHeight
        ? (targetHeight / targetWidth) * 100
        : (height / width) * 100;

    figureStyle = {
      position: 'relative',
      width: '100%',
      paddingTop: `${aspectRatio}%`,
    };
    imageProps.fill = true;
    imageProps.style = { objectFit: 'cover' };
  } else {
    figureStyle = {
      position: 'relative',
      width: '100%',
      height: 'auto',
    };
    imageProps.width = width;
    imageProps.height = height;
    imageProps.style = {
      width: '100%',
      height: 'auto',
      objectFit: fillMedia ? 'cover' : 'contain',
    };
  }

  const priority = forceLoad;
  const loading = forceLoad ? undefined : 'lazy';

  const imageObject = (
    <Image
      src={src}
      alt={alt}
      {...imageProps}
      priority={priority}
      fetchPriority={fetchPriority}
      loading={loading}
      sizes={resolvedSizes}
      quality={quality}
      placeholder={blurPlaceholder ? 'blur' : 'empty'}
      {...(blurPlaceholder ? { blurDataURL: blurPlaceholder } : {})}
    />
  );

  // ─── Parallax variant ─────────────────────────────────────────────────────
  if (parallax) {
    return (
      <ParallaxFigure
        className={className}
        style={figureStyle}
        parentRef={parentRef}
        a11yProps={a11yProps}
        motionProps={revealMotionProps}
        extraProps={props}
      >
        {imageObject}
      </ParallaxFigure>
    );
  }

  // ─── Swiper parallax variant ──────────────────────────────────────────────
  if (swiperParallax) {
    return (
      <motion.figure
        className={className}
        style={figureStyle}
        {...a11yProps}
        {...revealMotionProps}
        {...props}
      >
        <div
          style={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            top: '0',
          }}
          data-swiper-parallax-x="40%"
        >
          {imageObject}
        </div>
      </motion.figure>
    );
  }

  // ─── Standard variant ─────────────────────────────────────────────────────
  return (
    <motion.figure
      className={className}
      style={figureStyle}
      {...a11yProps}
      {...revealMotionProps}
      {...props}
    >
      {imageObject}
    </motion.figure>
  );
}
